import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { client } from "@/lib/api";

const categories = ["refrigeration", "cooking", "warewashing", "ice", "other"];

const lines = value => value.split("\n").map(line => line.trim()).filter(Boolean);
const toLines = list => (list || []).join("\n");

const fieldToLine = field => (field.options || []).length
  ? `${field.label} | ${field.options.join(", ")}`
  : field.label;

const lineToField = (line, existing) => {
  const [rawLabel, rawOptions] = line.split("|");
  const label = rawLabel.trim();
  const options = (rawOptions || "").split(",").map(option => option.trim()).filter(Boolean);
  const base = existing[label] || { label, input_type: "text", required: false, unit: "" };
  return { ...base, label, input_type: options.length ? "select" : "text", options };
};
const slug = value => value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const uniqueId = (base, taken) => {
  const root = slug(base) || "item";
  let candidate = root;
  let n = 2;
  while (taken.includes(candidate)) { candidate = `${root}_${n}`; n += 1; }
  return candidate;
};

let rowKeySeq = 0;
const nextRowKey = () => ++rowKeySeq;

export default function EquipmentTypes() {
  const [types, setTypes] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("refrigeration");
  const [fields, setFields] = useState("");
  const [phrases, setPhrases] = useState("");
  const [parts, setParts] = useState("");
  const [editing, setEditing] = useState(null);
  const load = async () => { try { setTypes((await client.get("/settings/equipment-types")).data); } catch { toast.error("Could not load equipment types"); } };
  useEffect(() => { load(); }, []);
  const add = async event => {
    event.preventDefault();
    const custom_fields = fields.split("\n").filter(Boolean).map(line => lineToField(line, {}));
    try { await client.post("/settings/equipment-types", { name, category, custom_fields, quick_phrases: phrases.split("\n").filter(Boolean), common_parts: parts.split("\n").filter(Boolean) }); toast.success("Equipment type created — set its checklist below"); setName(""); setFields(""); setPhrases(""); setParts(""); const fresh = (await client.get("/settings/equipment-types")).data; setTypes(fresh); const created = fresh.find(item => item.name === name.trim().toLowerCase()); if (created) startEdit(created); } catch (e) { toast.error(e?.response?.data?.detail || "Could not create type"); }
  };
  const toggle = async item => { await client.patch(`/settings/equipment-types/${item.id}`, { active: !item.active }); load(); };

  const startEdit = item => setEditing({
    id: item.id,
    name: item.name,
    category: item.category,
    fields: toLines((item.custom_fields || []).map(fieldToLine)),
    phrases: toLines(item.quick_phrases),
    parts: toLines(item.common_parts),
    groups: (item.checklist_groups || []).map(group => ({
      key: nextRowKey(), id: group.id, label: group.label,
      items: (group.items || []).map(check => ({ key: nextRowKey(), id: check.id, label: check.label })),
    })),
    readings: (item.readings || []).map(spec => ({
      key: nextRowKey(), id: spec.id, label: spec.label, unit: spec.unit || "",
      min: spec.min ?? "", max: spec.max ?? "",
    })),
    naReasons: toLines(item.na_reasons),
    captions: toLines(item.photo_captions),
  });

  const patchEditing = changes => setEditing(current => ({ ...current, ...changes }));
  const patchGroups = updater => setEditing(current => ({ ...current, groups: updater(current.groups) }));

  const addGroup = () => patchGroups(groups => [...groups, { key: nextRowKey(), id: "", label: "", items: [] }]);
  const removeGroup = index => patchGroups(groups => groups.filter((_, i) => i !== index));
  const moveGroup = (index, delta) => patchGroups(groups => {
    const target = index + delta;
    if (target < 0 || target >= groups.length) return groups;
    const next = [...groups];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const setGroupLabel = (index, label) => patchGroups(groups =>
    groups.map((group, i) => i === index ? { ...group, label } : group));
  const addCheck = index => patchGroups(groups =>
    groups.map((group, i) => i === index ? { ...group, items: [...group.items, { key: nextRowKey(), id: "", label: "" }] } : group));
  const removeCheck = (groupIndex, checkIndex) => patchGroups(groups =>
    groups.map((group, i) => i === groupIndex
      ? { ...group, items: group.items.filter((_, j) => j !== checkIndex) } : group));
  const setCheckLabel = (groupIndex, checkIndex, label) => patchGroups(groups =>
    groups.map((group, i) => i === groupIndex
      ? { ...group, items: group.items.map((check, j) => j === checkIndex ? { ...check, label } : check) }
      : group));

  const patchReadings = updater => setEditing(current => ({ ...current, readings: updater(current.readings) }));
  const addReading = () => patchReadings(list => [...list, { key: nextRowKey(), id: "", label: "", unit: "", min: "", max: "" }]);
  const removeReading = index => patchReadings(list => list.filter((_, i) => i !== index));
  const setReading = (index, key, value) => patchReadings(list =>
    list.map((spec, i) => i === index ? { ...spec, [key]: value } : spec));

  const groupCount = editing?.groups?.length || 0;
  const checkCount = (editing?.groups || []).reduce((total, group) => total + group.items.length, 0);

  const saveEdit = async event => {
    event.preventDefault();
    const existing = types.find(item => item.id === editing.id);
    const byLabel = Object.fromEntries((existing?.custom_fields || []).map(field => [field.label, field]));
    const groupIds = [];
    const checklist_groups = editing.groups
      .filter(group => group.label.trim())
      .map(group => {
        const id = group.id || uniqueId(group.label, groupIds);
        groupIds.push(id);
        const checkIds = [];
        return {
          id, label: group.label.trim(),
          items: group.items.filter(check => check.label.trim()).map(check => {
            const checkId = check.id || uniqueId(check.label, checkIds);
            checkIds.push(checkId);
            return { id: checkId, label: check.label.trim() };
          }),
        };
      });
    const readingIds = [];
    const readings = editing.readings
      .filter(spec => spec.label.trim())
      .map(spec => {
        const id = spec.id || uniqueId(spec.label, readingIds);
        readingIds.push(id);
        return {
          id, label: spec.label.trim(), unit: spec.unit.trim(),
          min: spec.min === "" ? null : Number(spec.min),
          max: spec.max === "" ? null : Number(spec.max),
        };
      });
    try {
      await client.patch(`/settings/equipment-types/${editing.id}`, {
        name: editing.name,
        category: editing.category,
        custom_fields: lines(editing.fields).map(line => lineToField(line, byLabel)),
        quick_phrases: lines(editing.phrases),
        common_parts: lines(editing.parts),
        checklist_groups,
        readings,
        na_reasons: lines(editing.naReasons),
        photo_captions: lines(editing.captions),
      });
      toast.success("Equipment type updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save this type");
    }
  };

  return (
    <div data-testid="equipment-types-page">
      <Toaster richColors position="top-right" />
      <div><div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Settings</div><h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>Equipment types</h1><p className="mt-1 text-[13.5px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>Create a new type, its fields, phrases, and common parts without code changes.</p></div>
      <form className="mt-7 bf-card" style={{ padding: "20px" }} onSubmit={add} data-testid="equipment-type-form"><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><Input className="rounded-[10px]" value={name} onChange={e => setName(e.target.value)} placeholder="Equipment type name" data-testid="equipment-type-name-input" /><select value={category} onChange={e => setCategory(e.target.value)} className="h-9 rounded-[10px] px-2 bg-white" style={{ border: "1px solid #E7EAEE" }} data-testid="equipment-type-category-select">{categories.map(value => <option key={value}>{value}</option>)}</select><textarea value={fields} onChange={e => setFields(e.target.value)} className="min-h-24 rounded-[10px] p-2.5 text-[13.5px]" style={{ border: "1px solid #E7EAEE" }} placeholder="Extra fields, one label per line" data-testid="equipment-type-fields-input" /><textarea value={phrases} onChange={e => setPhrases(e.target.value)} className="min-h-24 rounded-[10px] p-2.5 text-[13.5px]" style={{ border: "1px solid #E7EAEE" }} placeholder="Quick work phrases, up to five" data-testid="equipment-type-phrases-input" /><textarea value={parts} onChange={e => setParts(e.target.value)} className="min-h-24 rounded-[10px] p-2.5 text-[13.5px] md:col-span-2" style={{ border: "1px solid #E7EAEE" }} placeholder="Common parts, one per line" data-testid="equipment-type-parts-input" /></div><Button className="mt-5 bf-btn-primary px-4" data-testid="save-equipment-type-button"><Plus size={15} className="mr-2" />Save type</Button><div className="mt-3 text-[12.5px] bf-help">A new type starts on the standard checklist. Create it, then use Edit below to set its own checks and readings.</div></form>

      {editing && (
        <form className="mt-6 bf-card" style={{ padding: "20px" }} onSubmit={saveEdit} data-testid="equipment-type-edit-form">
          <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold mb-3">Editing {editing.name}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input className="rounded-[10px]" value={editing.name}
                   onChange={e => patchEditing({ name: e.target.value })}
                   placeholder="Equipment type name" data-testid="edit-type-name-input" />
            <select value={editing.category} onChange={e => patchEditing({ category: e.target.value })}
                    className="h-9 px-2 bf-input" data-testid="edit-type-category-select">
              {categories.map(value => <option key={value}>{value}</option>)}
            </select>
            <textarea value={editing.fields} onChange={e => patchEditing({ fields: e.target.value })}
                      className="min-h-24 p-2.5 text-[13.5px] bf-input"
                      placeholder="Extra fields, one per line. Add | option, option for a dropdown" data-testid="edit-type-fields-input" />
            <textarea value={editing.phrases} onChange={e => patchEditing({ phrases: e.target.value })}
                      className="min-h-24 p-2.5 text-[13.5px] bf-input"
                      placeholder="Quick work phrases, up to five" data-testid="edit-type-phrases-input" />
            <textarea value={editing.parts} onChange={e => patchEditing({ parts: e.target.value })}
                      className="min-h-24 p-2.5 text-[13.5px] md:col-span-2 bf-input"
                      placeholder="Common parts, one per line" data-testid="edit-type-parts-input" />
          </div>

          <div className="mt-7" data-testid="edit-type-groups">
            <div className="flex items-center justify-between">
              <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold">Checklist groups</div>
              <Button type="button" variant="outline" className="h-9 bf-btn-ghost"
                      onClick={addGroup} data-testid="add-group-button">
                <Plus size={14} className="mr-2" />Add group
              </Button>
            </div>
            <div className="mt-1 text-[12.5px] bf-help">
              Leave this empty to keep the standard checklist for this category.
            </div>
            <div className="mt-3 space-y-3">
              {editing.groups.map((group, groupIndex) => (
                <div key={group.key} className="rounded-[10px] p-3 bf-input" data-testid={`group-row-${groupIndex}`}>
                  <div className="flex items-center gap-2">
                    <Input className="rounded-[10px] flex-1" value={group.label}
                           onChange={e => setGroupLabel(groupIndex, e.target.value)}
                           placeholder="Group name, for example Safety and isolation"
                           data-testid={`group-label-${groupIndex}`} />
                    <Button type="button" variant="outline" className="h-9 w-9 p-0 bf-btn-ghost"
                            onClick={() => moveGroup(groupIndex, -1)} data-testid={`group-up-${groupIndex}`}>
                      <ChevronUp size={15} />
                    </Button>
                    <Button type="button" variant="outline" className="h-9 w-9 p-0 bf-btn-ghost"
                            onClick={() => moveGroup(groupIndex, 1)} data-testid={`group-down-${groupIndex}`}>
                      <ChevronDown size={15} />
                    </Button>
                    <Button type="button" variant="outline" className="h-9 w-9 p-0 bf-btn-ghost"
                            onClick={() => removeGroup(groupIndex)} data-testid={`group-remove-${groupIndex}`}>
                      <Trash2 size={15} />
                    </Button>
                  </div>
                  <div className="mt-2 pl-3 space-y-2">
                    {group.items.map((check, checkIndex) => (
                      <div key={check.key} className="flex items-center gap-2">
                        <Input className="rounded-[10px] flex-1 text-[13.5px]" value={check.label}
                               onChange={e => setCheckLabel(groupIndex, checkIndex, e.target.value)}
                               placeholder="Check, for example Door seals sound"
                               data-testid={`check-label-${groupIndex}-${checkIndex}`} />
                        <Button type="button" variant="outline" className="h-9 w-9 p-0 bf-btn-ghost"
                                onClick={() => removeCheck(groupIndex, checkIndex)}
                                data-testid={`check-remove-${groupIndex}-${checkIndex}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" className="h-9 bf-btn-ghost"
                            onClick={() => addCheck(groupIndex)} data-testid={`add-check-${groupIndex}`}>
                      <Plus size={14} className="mr-2" />Add check
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-7" data-testid="edit-type-readings">
            <div className="flex items-center justify-between">
              <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold">Readings</div>
              <Button type="button" variant="outline" className="h-9 bf-btn-ghost"
                      onClick={addReading} data-testid="add-reading-button">
                <Plus size={14} className="mr-2" />Add reading
              </Button>
            </div>
            <div className="mt-1 text-[12.5px] bf-help">
              A reading outside its range becomes a failure on the job sheet.
            </div>
            <div className="mt-3 space-y-2">
              {editing.readings.map((spec, index) => (
                <div key={spec.key} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-center"
                     data-testid={`reading-row-${index}`}>
                  <Input className="rounded-[10px] md:col-span-2 text-[13.5px]" value={spec.label}
                         onChange={e => setReading(index, "label", e.target.value)}
                         placeholder="Reading name" data-testid={`reading-label-${index}`} />
                  <Input className="rounded-[10px] text-[13.5px]" value={spec.unit}
                         onChange={e => setReading(index, "unit", e.target.value)}
                         placeholder="Unit" data-testid={`reading-unit-${index}`} />
                  <Input className="rounded-[10px] text-[13.5px]" type="number" value={spec.min}
                         onChange={e => setReading(index, "min", e.target.value)}
                         placeholder="Minimum" data-testid={`reading-min-${index}`} />
                  <div className="flex items-center gap-2">
                    <Input className="rounded-[10px] text-[13.5px]" type="number" value={spec.max}
                           onChange={e => setReading(index, "max", e.target.value)}
                           placeholder="Maximum" data-testid={`reading-max-${index}`} />
                    <Button type="button" variant="outline" className="h-9 w-9 p-0 bf-btn-ghost shrink-0"
                            onClick={() => removeReading(index)} data-testid={`reading-remove-${index}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold mb-2">Not applicable reasons</div>
              <textarea value={editing.naReasons} onChange={e => patchEditing({ naReasons: e.target.value })}
                        className="w-full min-h-24 p-2.5 text-[13.5px] bf-input"
                        placeholder="One reason per line" data-testid="edit-type-na-reasons-input" />
            </div>
            <div>
              <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold mb-2">Photo captions</div>
              <textarea value={editing.captions} onChange={e => patchEditing({ captions: e.target.value })}
                        className="w-full min-h-24 p-2.5 text-[13.5px] bf-input"
                        placeholder="One caption per line" data-testid="edit-type-captions-input" />
            </div>
          </div>

          <div className="mt-6 text-[13.5px]" data-testid="edit-type-summary">
            {groupCount} group{groupCount === 1 ? "" : "s"} · {checkCount} check{checkCount === 1 ? "" : "s"}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button className="bf-btn-primary px-4" data-testid="save-type-edit-button">Save changes</Button>
            <Button type="button" variant="outline" className="bf-btn-ghost px-4"
                    onClick={() => setEditing(null)} data-testid="cancel-type-edit-button">Cancel</Button>
          </div>
        </form>
      )}

      <div className="mt-6 bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }}>{types.map(item => <div key={item.id} className="p-4 border-b flex justify-between gap-3 items-center" style={{ borderColor: "#EFF1F4" }} data-testid={`equipment-type-${item.name}`}><div><strong className="text-[15px]">{item.name}</strong><span className="ml-2 mono text-[11px]" style={{ color: "#5B6472" }}>{item.category}</span><div className="mt-1 text-[13px]" style={{ color: "#5B6472" }}>{item.custom_fields.length} fields · {item.quick_phrases.length} phrases · {item.common_parts.length} parts · {(item.checklist_groups || []).length} groups</div></div><div className="flex items-center gap-2"><Button variant="outline" className="h-9 bf-btn-ghost" onClick={() => startEdit(item)} data-testid={`equipment-type-edit-${item.id}`}><Pencil size={14} className="mr-2" />Edit</Button><Button variant="outline" className="h-9 bf-btn-ghost" onClick={() => toggle(item)} data-testid={`toggle-equipment-type-${item.id}`}>{item.active ? "Deactivate" : "Activate"}</Button></div></div>)}</div>
    </div>
  );
}
